import {Modal, Tag} from 'antd';
import {format, parseISO} from 'date-fns';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';

const ProductDetailScreen = ({viewModal, setViewModal}) => {
  const record = viewModal.record;

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeImage, setActiveImage] = useState(null);

  const toDropboxRawUrl = url => {
    if (!url || typeof url !== 'string') {return '';}
  
    return url
      .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');
  };

  useEffect(() => {
    if (record?.variants?.length) {
      setSelectedVariant(record.variants[0]);
      setActiveImage(record.variants[0]?.images?.[0]);
    }
  }, [record]);

  if (!record) {return null;}

  const rawDate = record?.createdOn;

  return (
    <Modal
      title={'Product Details'}
      open={viewModal.visible}
      footer={null}
      width={1000}
      onCancel={() => setViewModal({visible: false, record: null})}
    >
      <div className={'bg-gray-50 md:p-6 p-2 rounded-xl space-y-6'}>

        {/* TOP SECTION */}
        <div className={'grid md:grid-cols-2 gap-8'}>

          {/* IMAGE GALLERY */}
          <div className={'space-y-4'}>
            <div className={'bg-white rounded-xl shadow-lg p-4'}>
              <img
                src={toDropboxRawUrl(activeImage)}
                alt={'product'}
                className={'w-full h-[360px] object-contain rounded-lg'}
              />
            </div>

            {selectedVariant?.images?.length > 1 && (
              <div className={'grid grid-cols-4 gap-3'}>
                {selectedVariant.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    className={`h-20 object-contain rounded-lg cursor-pointer border transition
                      ${
                  activeImage === img
                    ? 'border-black shadow-md'
                    : 'border-gray-200 hover:shadow'
                  }`}
                    type={'button'}
                  >
                    <img
                      alt={''}
                      src={toDropboxRawUrl(img)}
                      className={'w-full h-full object-contain'}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* PRODUCT INFO */}
          <div className={'space-y-5'}>
            <h2 className={'text-2xl font-semibold'}>{record.productName}</h2>
            <p className={'text-gray-600'}>{record.description}</p>

            <div className={'grid grid-cols-2 gap-3 text-sm text-gray-700'}>
              <p><strong>EAN:</strong> {record.eanCode}</p>
              <p><strong>Brand:</strong> {record.brand || '-'}</p>
              <p><strong>Category:</strong> {record.category || '-'}</p>
            </div>

            {/* PRICE */}
            <div className={'flex items-center gap-4 text-base'}>
              <p><strong>Price: </strong>₹{record.price}</p>
              {/* <span className={'font-semibold'}>Price: ₹{record.price}</span> */}
              {record.discount > 0 && (
                <>
                  <Tag color={'green'}> {record.discount}% OFF</Tag>
                  <p><strong>Discounted Price: </strong>₹{Math.round(record.price * (1 - record.discount / 100))}</p>
                </>
              )}
            </div>

            {/* Variants */}
            <div className={'bg-white rounded-xl shadow p-5'}>
              <h3 className={'font-bold text-lg mb-2'}>Variants</h3>

              <div className={'space-y-6'}>
                {record.variants.map((variant, vIdx) => (
                  <div key={vIdx} className={'border border-primary-50 rounded-xl p-4'}>
        
                    {/* Color Header */}
                    <div className={'flex items-center gap-3 mb-3'}>
                      <p className={'text-base font-semibold '}>color :</p> 
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: variant.color?.toLowerCase(),
                          border: '1px solid #ccc',
                        }}
                      />
                      <h4 className={'font-semibold md:text-base'}>
                        {variant.color}
                      </h4>
                    </div>

                    {/* Sizes for this color */}
                    {variant.sizes?.length > 0 ? (
                      <div className={' gap-0'}>
                        {variant.sizes.map((s, sIdx) => (
                          <div
                            key={sIdx}
                            className={'rounded-lg text-sm'}
                          >
                            <span className={'pr-2 text-base font-semibold'}>Size : {s.size}</span>
                            <span
                              className={
                                s.quantity > 0
                                  ? 'text-green-600 font-semibold'
                                  : 'text-red-500 font-semibold'
                              }
                            >
                              {s.quantity > 0 ? `(${s.quantity} left)` : 'Out of Stock'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={'text-sm text-gray-500'}>
            No sizes available for this color
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FEATURES */}
        {record.features?.length > 0 && (
          <div className={'bg-white rounded-xl shadow p-5'}>
            <h3 className={'font-bold text-lg mb-3'}>Features</h3>
            <ul className={'list-disc ml-6 text-gray-600 space-y-1'}>
              {record.features.map((feature, idx) => (
                <li key={idx}>{feature}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

ProductDetailScreen.propTypes = {
  viewModal: PropTypes.shape({
    visible: PropTypes.bool.isRequired,
    record: PropTypes.object,
  }).isRequired,
  setViewModal: PropTypes.func.isRequired,
};

export default ProductDetailScreen;
